import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Banner, Button, Card, CardHeader, EmptyState, Spinner } from '../ui';

// Read a File as base64 (no data-URL prefix) so the cloud function can unzip
// and analyze it server-side.
const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// Cloud functions return notes as plain JSON, so dates arrive as
// { __type: 'Date', iso } and fields are plain properties.
const noteDate = (note) => (note.sermonDate && note.sermonDate.iso) || note.createdAt;

/**
 * The review step: everything the AI extracted, editable, so a person signs
 * off before anything would ever reach the congregation.
 */
const ReviewEditor = ({ note, onSaved, onCancel, onError }) => {
    const [title, setTitle] = useState(note.title || '');
    const [summary, setSummary] = useState(note.summary || '');
    const [verses, setVerses] = useState(note.verses || []);
    const [saving, setSaving] = useState(false);

    const setVerse = (index, field, value) =>
        setVerses((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));

    const handleSave = async () => {
        setSaving(true);
        try {
            const saved = await Parse.Cloud.run('pastorsUpdateSermonNote', {
                sermonNoteId: note.objectId,
                title,
                summary,
                verses,
            });
            onSaved(saved);
        } catch (err) {
            console.error('Error saving sermon note:', err);
            onError(err.message || 'We could not save the sermon notes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <CardHeader
                title="Review the extracted notes"
                description="Check what the AI pulled from the deck and correct anything before it's used."
            />
            <div className="space-y-5 px-5 py-4">
                <div>
                    <label htmlFor="sermon-title" className="block text-sm font-medium text-neutral-800">
                        Sermon title
                    </label>
                    <input
                        id="sermon-title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-1 w-full"
                    />
                </div>

                <div>
                    <label htmlFor="sermon-summary" className="block text-sm font-medium text-neutral-800">
                        Summary
                    </label>
                    <textarea
                        id="sermon-summary"
                        rows={4}
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        className="mt-1 w-full"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <span className="block text-sm font-medium text-neutral-800">Bible verses</span>
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setVerses((prev) => [...prev, { reference: '', text: '' }])}
                        >
                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                            Add verse
                        </Button>
                    </div>
                    {verses.length === 0 ? (
                        <p className="mt-2 text-sm text-neutral-500">
                            No verses were found in the deck. Add them by hand if the sermon had any.
                        </p>
                    ) : (
                        <ul className="mt-2 space-y-3">
                            {verses.map((verse, index) => (
                                <li key={index} className="rounded-md border border-neutral-200 p-3">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            aria-label={`Verse ${index + 1} reference`}
                                            placeholder="John 3:16"
                                            value={verse.reference}
                                            onChange={(e) => setVerse(index, 'reference', e.target.value)}
                                            className="w-full font-medium"
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            aria-label={`Remove verse ${index + 1}`}
                                            onClick={() => setVerses((prev) => prev.filter((_, i) => i !== index))}
                                        >
                                            <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                        </Button>
                                    </div>
                                    <textarea
                                        aria-label={`Verse ${index + 1} text`}
                                        rows={2}
                                        placeholder="Verse text as it should appear (optional)"
                                        value={verse.text}
                                        onChange={(e) => setVerse(index, 'text', e.target.value)}
                                        className="mt-2 w-full"
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="primary" disabled={saving} onClick={handleSave}>
                        {saving && <Spinner className="h-4 w-4" />}
                        {saving ? 'Saving…' : 'Save notes'}
                    </Button>
                    <Button variant="secondary" disabled={saving} onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </div>
        </Card>
    );
};

const SermonNotesManager = () => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [editing, setEditing] = useState(null); // a note JSON, while reviewing
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await Parse.Cloud.run('pastorsListSermonNotes');
            setNotes(data || []);
        } catch (err) {
            console.error('Error fetching sermon notes:', err);
            setError('We could not load the sermon notes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        setAnalyzing(true);
        setError('');
        setSuccessMessage('');

        const date = e.target.date.value;
        const file = e.target.file.files[0];
        const form = e.target;

        if (!file) {
            setError('Please choose a PowerPoint file.');
            setAnalyzing(false);
            return;
        }

        try {
            const fileBase64 = await fileToBase64(file);
            const created = await Parse.Cloud.run('pastorsExtractSermonNotes', {
                date,
                fileName: file.name,
                fileBase64,
            });

            setSuccessMessage(
                `Found ${(created.verses || []).length} verse${(created.verses || []).length === 1 ? '' : 's'} across ${created.slideCount} slides. Review them below.`
            );
            form.reset();
            setEditing(created);
            fetchNotes();
        } catch (err) {
            console.error('Error analyzing sermon deck:', err);
            setError(err.message || 'We could not analyze the deck. Please try again.');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleDelete = async (note) => {
        if (!window.confirm(`Delete "${note.title}"? This can't be undone.`)) {
            return;
        }

        try {
            await Parse.Cloud.run('pastorsDeleteSermonNote', { sermonNoteId: note.objectId });
            if (editing && editing.objectId === note.objectId) setEditing(null);
            setSuccessMessage('Sermon notes deleted.');
            fetchNotes();
        } catch (err) {
            console.error('Error deleting sermon note:', err);
            setError(err.message || 'We could not delete the sermon notes. Please try again.');
        }
    };

    return (
        <div className="space-y-4">
            {error && <Banner tone="danger">{error}</Banner>}
            {successMessage && <Banner tone="success">{successMessage}</Banner>}
            <Banner tone="info">
                Sermon notes stay here in the forms system for now — showing them in the member app is
                coming once we've validated the extraction.
            </Banner>

            <Card>
                <CardHeader
                    title="Upload a sermon deck"
                    description="We'll read the slides and pull out the Bible verses and a short summary for your review."
                />
                <form onSubmit={handleUpload} className="grid gap-4 px-5 py-4 sm:grid-cols-2">
                    <div>
                        <label htmlFor="sermon-date" className="block text-sm font-medium text-neutral-800">
                            Sermon date
                        </label>
                        <input
                            id="sermon-date"
                            type="date"
                            name="date"
                            required
                            className="mt-1"
                            defaultValue={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div>
                        <label htmlFor="sermon-file" className="block text-sm font-medium text-neutral-800">
                            PowerPoint (.pptx)
                        </label>
                        <input id="sermon-file" type="file" name="file" accept=".pptx" required className="mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Button type="submit" variant="primary" disabled={analyzing}>
                            {analyzing && <Spinner className="h-4 w-4" />}
                            {analyzing ? 'Reading the deck…' : 'Upload & extract'}
                        </Button>
                        {analyzing && (
                            <p className="mt-2 text-xs text-neutral-500">
                                This can take up to a minute for a large deck.
                            </p>
                        )}
                    </div>
                </form>
            </Card>

            {editing && (
                <ReviewEditor
                    note={editing}
                    onSaved={(saved) => {
                        setEditing(null);
                        setSuccessMessage(`Saved "${saved.title}".`);
                        fetchNotes();
                    }}
                    onCancel={() => setEditing(null)}
                    onError={setError}
                />
            )}

            <Card className="overflow-hidden">
                <CardHeader title="Sermon notes" description="Newest first. All drafts — nothing is in the app yet." />
                {loading ? (
                    <EmptyState title="Loading sermon notes…" />
                ) : notes.length === 0 ? (
                    <EmptyState title="No sermon notes yet" description="Upload your first sermon deck above." />
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {notes.map((note) => (
                            <li
                                key={note.objectId}
                                className="flex flex-col gap-2 px-5 py-3.5 hover:bg-neutral-50/70 sm:flex-row sm:items-center sm:justify-between"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-neutral-900">{note.title}</p>
                                    <p className="mt-0.5 text-xs text-neutral-500">
                                        {formatDate(noteDate(note))} · {(note.verses || []).length} verse
                                        {(note.verses || []).length === 1 ? '' : 's'}
                                        {note.sourceFileName ? ` · from ${note.sourceFileName}` : ''}
                                    </p>
                                </div>
                                <div className="flex flex-shrink-0 items-center gap-2">
                                    <Button size="sm" variant="secondary" onClick={() => setEditing(note)}>
                                        Review & edit
                                    </Button>
                                    <Button size="sm" variant="danger" onClick={() => handleDelete(note)}>
                                        Delete
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </div>
    );
};

export default SermonNotesManager;
